import type { ComponentChildren, JSX } from "preact";
import { useEffect, useRef } from "preact/hooks";
import { FieldError } from "./field-error";
import { clearFieldError, useFormErrors } from "./form";

type InputProps = JSX.IntrinsicElements["input"] & {
  label: ComponentChildren;
  aside?: ComponentChildren;
  error?: string;
};

export function Input({
  label,
  aside,
  error: errorProp,
  class: className,
  autofocus,
  name,
  onInput,
  ...props
}: InputProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const formErrors = useFormErrors();
  const fieldName = typeof name === "string" ? name : undefined;
  const error =
    errorProp ?? (fieldName ? formErrors?.value[fieldName] : undefined);

  useEffect(() => {
    if (autofocus) {
      inputRef.current?.focus();
    }
  }, [autofocus]);

  return (
    <label class="flex flex-col gap-1">
      <span class="flex justify-between items-center text-sm">
        <span class="font-medium text-text-secondary">{label}</span>
        {aside}
      </span>
      <input
        ref={inputRef}
        name={name}
        aria-invalid={!!error}
        onInput={(e) => {
          clearFieldError(formErrors, fieldName);
          onInput?.(e);
        }}
        class={`block w-full rounded-lg border border-border px-3 py-2 bg-surface text-text placeholder:text-text-subtle hover:border-text-subtle focus:ring-ring transition-colors ${className || ""}`}
        {...props}
      />
      <FieldError>{error}</FieldError>
    </label>
  );
}
